<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': menuIsExpanded },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  v-if="mode === 'dark' && menuIsExpanded"
                  :src="appDarkLogoImage"
                  height="44"
                  :alt="`${appName} logo`"
                />
                <b-img
                  v-else-if="mode === 'dark' && !menuIsExpanded"
                  :src="appDarkLogoImageMini"
                  height="44"
                  :alt="`${appName} logo`"
                />
                <b-img
                  v-else-if="mode === 'light' && !menuIsExpanded"
                  :src="appLogoImageMini"
                  height="44"
                  :alt="`${appName} logo`"
                />
                <b-img
                  v-else
                  :src="appLogoImage"
                  height="44"
                  :alt="`${appName} logo`"
                />
              </span>
              <!-- <h2
                v-if="mode !== 'dark'"
                class="brand-text"
              >
                {{ appName }}
              </h2> -->
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="navMenuItemsFiltered"
        class="navigation navigation-main"
      />
      <!-- <footer class="navigation-footer">
        <ul>
          <li class="nav-item">
            <b-link
              class="d-flex align-items-center"
            >
              <feather-icon icon="LogOutIcon" />
              <span class="menu-title text-truncate">Logout</span>
            </b-link>
          </li>
        </ul>
      </footer> -->
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import navMenuItems from '@/navigation/vertical'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const {
      appName, appLogoImage, appDarkLogoImage, appLogoImageMini, appDarkLogoImageMini,
    } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      appDarkLogoImage,
      appLogoImageMini,
      appDarkLogoImageMini,
    }
  },
  computed: {
    mode() {
      return this.$store.state.appConfig.layout.skin
    },
    menuIsExpanded() {
      return (!this.isVerticalMenuCollapsed || (this.isVerticalMenuCollapsed && this.isMouseHovered))
    },
    navMenuItemsFiltered() {
      // show selective nav items depending on if user is fully onboarded
      const userData = this.$store.getters['users/getCurrentUser']
      let newNavMenuItems = []
      if (userData && userData.domains.length > 0 && userData.domains[0].conversion_values != null) {
        newNavMenuItems = navMenuItems.all
      } else {
        newNavMenuItems = navMenuItems.onboarding
      }
      return newNavMenuItems
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu-content {
  display: flex;
  flex-direction: column;
}
.navigation-main {
  flex-grow: 1;
  height: 100%;
  overflow-y: auto;
}
.navigation-footer {
  flex-grow: 0;
}
.brand-logo img{
  max-width: 100% !important;
}
</style>
